import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { CartType, CheckoutFlowEnum, ProductTypeEnum } from "../types/types";
import {
  ALL_CONSUMER_PRODUCTS,
  AOV_PRODUCTS,
  CAR_PRODUCT,
  CART_STORE,
  CONSUMER_PRODUCTS,
} from "../constants/constant";
import {
  addProductToCart,
  clearCart,
  deleteProductFromCart,
  getCartItemList,
} from "services/api/cart";

const initialCartState = {
  cartItemList: [],
  checkoutCartItems: [],
  checkoutFlow: undefined,
  currentFlow: CheckoutFlowEnum.NORMAL,
  entryDate: undefined,
  showDisclaimerPage: false,
};

export const useCartStore = create(
  devtools(
    persist<CartType>(
      (set, get) => ({
        ...initialCartState,
        getCartItemList: async () => {
          try {
            const checkoutItems = get().checkoutCartItems;
            const cartItemList = await getCartItemList();
            set(() => ({
              cartItemList: checkoutItems?.length
                ? [
                    ...cartItemList
                      .filter((item) =>
                        checkoutItems.some(
                          (checkoutItem) => checkoutItem.id === item.id
                        )
                      )
                      .map((item) => ({
                        ...item,
                        inCheckoutCart: true,
                      })),
                    ...cartItemList.filter(
                      (item) =>
                        !checkoutItems.some(
                          (checkoutItem) => checkoutItem.id === item.id
                        )
                    ),
                  ]
                : cartItemList,
            }));
            return cartItemList;
          } catch (err: unknown) {
            console.error(err);
            throw err;
          }
        },
        addToCart: async (insurance_product) => {
          try {
            await addProductToCart(insurance_product);
            await get().getCartItemList();
          } catch (err) {
            console.error(err);
            throw err;
          }
        },
        deleteFromCart: async (cartItemId) => {
          try {
            await deleteProductFromCart(cartItemId);
            await get().getCartItemList();
          } catch (err) {
            console.error(err);
            throw err;
          }
        },
        clearCartItems: async () => {
          try {
            await clearCart();
            await get().getCartItemList();
          } catch (err: unknown) {
            console.error(err);
            throw err;
          }
        },
        setEntryDate: (entryDate) => {
          set(() => ({
            entryDate,
          }));
        },
        prepareCheckoutFlow: async () => {
          const cartItems = get().cartItemList;
          return new Promise((resolve) => {
            const car =
              cartItems.filter((item) =>
                CAR_PRODUCT.includes(
                  item.insurance_product.type as ProductTypeEnum
                )
              ) ?? undefined;
            const consumer =
              cartItems.filter((item) =>
                CONSUMER_PRODUCTS.includes(
                  item.insurance_product.type as ProductTypeEnum
                )
              ) ?? undefined;
            const aov =
              cartItems.filter((item) =>
                AOV_PRODUCTS.includes(
                  item.insurance_product.type as ProductTypeEnum
                )
              ) ?? undefined;
            const normal =
              cartItems.filter(
                (item) =>
                  ![...ALL_CONSUMER_PRODUCTS, ...AOV_PRODUCTS].includes(
                    item.insurance_product.type as ProductTypeEnum
                  )
              ) ?? undefined;
            set(() => ({
              checkoutFlow: {
                car,
                consumer,
                aov,
                normal,
              },
            }));
            resolve();
          });
        },
        setCheckoutCartItems: async () => {
          return new Promise((resolve) => {
            const checkoutFlow = get().checkoutFlow;
            // Check each type in priority order
            const itemType = checkoutFlow?.car?.length
              ? CheckoutFlowEnum.CAR
              : checkoutFlow?.consumer?.length
              ? CheckoutFlowEnum.CONSUMER
              : checkoutFlow?.aov?.length
              ? CheckoutFlowEnum.AOV
              : checkoutFlow?.normal?.length
              ? CheckoutFlowEnum.NORMAL
              : undefined;

            set({
              showDisclaimerPage:
                Object.values({
                  car: checkoutFlow?.car,
                  consumer: checkoutFlow?.consumer,
                  aov: checkoutFlow?.aov,
                  normal: checkoutFlow?.normal,
                }).filter((arr) => arr && arr.length > 0).length > 1,
              currentFlow: itemType as CheckoutFlowEnum,
              checkoutCartItems:
                checkoutFlow?.[itemType as CheckoutFlowEnum] ?? [],
              checkoutFlow: itemType
                ? {
                    ...checkoutFlow,
                    [itemType]: [],
                  }
                : checkoutFlow,
            });
            resolve();
          });
        },
        clearCheckoutFlow: () => {
          set(() => ({
            checkoutCartItems: [],
            checkoutFlow: undefined,
            currentFlow: CheckoutFlowEnum.NORMAL,
            showDisclaimerPage: false,
          }));
        },
        clearCart: () => {
          set(() => initialCartState);
        },
      }),
      {
        name: CART_STORE,
        storage: createJSONStorage(() => localStorage),
      }
    )
  )
);
